/** Modulos necesarios del contenedor */
import React, { useContext, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  GTIWebTextField, GTIWebButton, CircularProgress,
  CheckCircle, Error, GTIWebAlert, Stack, Box, DoNotDisturbAlt,
} from "@gti-controls-v2/web-core";
import { AuthContext } from "../../providers/auth";
import { LocaleContext } from "../../providers/locale";
import { useTranslation } from "../../hooks/useTranslation";
import { paths } from "../../common/constants/Rutas";
import loginStyles from "../../common/styles/Login";
import useInputValidator from "../../hooks/useInputValidator";
import LoginLayout from "../../layouts/Login/LoginLayout";
import customTheme from "../../theme";
import apiConfig from "../../config/api";
import { useMediaQueryCustom } from "../../hooks/useMediaQueryCustom/useMediaQueryCustom";
import { localStorageData } from "../../common/constants/Settings";

/** Constante del contenedor */
export const Login = () => {

  /** Hooks  */
  const styles = loginStyles;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { login } = useContext(AuthContext);
  const { changeLocale } = useContext(LocaleContext);
  const { validateInput: inputValidator } = useInputValidator();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { isMobile480, isTablet768, isTablet1024 } = useMediaQueryCustom();

  /** Asigna el Meta Title fijo a la página */
  document.title = `${t("sistema.login.titulos.iniciarSesion")} ${t("sistema.titulos.aplicacion")}`;

  /** Estado de la forma */
  const [loginData, setLoginData] = useState({
    username: searchParams.get("correo") || "",
    password: "",
    userState: null,
    passwordState: null,
    userLegend: t("sistema.login.titulos.emailLabel"),
    userIcon: null,
    passwordLegend: null,
  });

  /** Acceso a Consumos */
  const onLogin = async (e) => {
    try {
      setIsLoading(true);
      if (e) e.preventDefault();
      if (loginData.username !== "" && loginData.password !== "") {

        const loginResponse = await fetchLogin();
        const loginData = await loginResponse.json();

        if (loginResponse.ok) {
          //TODO: A futuro se podria manejar diferentes idiomas para las etiqueas,, se puede cambiar aquí
          //de la respuesta del API login podria indicar el idioma, actualmente solo se asigna 
          changeLocale("es-MX");

          const clientesResponse = await fetchClientes(loginData?.UsuarioID, loginData?.access_token);
          const clientesData = await clientesResponse.json();

          if (clientesData?.length > 0) {
            //TODO: A futuro se puede ajustar la duración del token, el API devuelve "expires_in": 31535999 seg = (1 año)
            const token_expires_at = new Date();
            token_expires_at.setSeconds(token_expires_at.getSeconds() + loginData?.expires_in);
            //para ajustar la duración del token en el frontend podemos asignar aqui un valor determinado.
            //token_expires_at.setSeconds(token_expires_at.getSeconds() + 15);

            const data = {
              token: loginData?.access_token,
              token_expires_in: loginData?.expires_in,
              token_expires_at: token_expires_at,
              usuarioNombre: loginData?.UsuarioNombre,
              usuarioID: loginData?.UsuarioID,
              sessionID: loginData?.SessionID,
              //TODO: Pendinete revisar diseño para ver si es valido tomar siempre el primer clienteID al que tenga derechos el usuario
              //en aplicación anterior estaba de esta manera.
              clienteID: clientesData[0]?.ClienteID,
              clienteNombre: clientesData[0]?.ClienteNombre,
            }

            login(data);
            //Inicializo cajaID en localStorage en cada login, para que siempre me pida elegir una caja al ingresar
            localStorage.setItem(localStorageData.CAJA_ID, 0);
            navigate(paths.Consumos, { replace: true });
          }
          else {
            setError({ message: t("sistema.errores.noClientes") });
          }

        }
        else {
          if (loginResponse.status === 400) {
            setError({ message: loginData.error_description });
          }
          else {
            setError({ message: (`${t("sistema.errores.fetchError")} (${loginResponse.status})`) });
          }
        }

      }
    } catch (err) {
      console.error('Error al realizar login:', err);
      setError({ message: t("sistema.errores.fetchError") });
    }
    finally {
      setIsLoading(false);
    }

  };
  /** Función que hace la petición al servidor para obtener el token de acceso */
  const fetchLogin = async () => {
    const response = await fetch(apiConfig.API_URL_AUTH, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
      body: `grant_type=password&username=${loginData.username}&password=${loginData.password}`,
    });

    return (response);
  }
  /** Función que hace la petición al servidor para obtener el listado de clientes a los cuales tiene acceso el usaurio
   * @param {usuarioID} loginData?.UsuarioID - identificador del usuario que devuelve la petición de fetchLogin.
   * @param {token} loginData?.access_token - token de acceso que devuelve la petición de fetchLogin.
   */
  const fetchClientes = async (usuarioID, token) => {
    const response = await fetch(apiConfig.API_URL_CLIENTES.replace("{usuarioID}", usuarioID), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
      },
    });

    return (response);
  }

  /** Función que se ejecuta cuando se cambia el input de username y password */
  const handleChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value.trim() });
  };

  /** Valida el input de usuario, si es valido se muestra el icono correcto, si no se muestra el icono de error */
  const validateInput = () => {
    const handleValidator = (objectResponse) => {
      if (objectResponse.isValid) {
        setLoginData({
          ...loginData,
          userLegend: t("sistema.login.titulos.emailLegend"),
          userState: "success",
          userIcon: (
            <CheckCircle style={{ color: customTheme.paleta.success40 }} />
          ),
        });
      } else {
        setLoginData({
          ...loginData,
          userLegend: objectResponse.text,
          userState: "error",
          userIcon: <Error style={{ color: customTheme.paleta.error40 }} />,
        });
      }
    };
    inputValidator({ text: loginData.username }, handleValidator);
  };

  /** UI */
  return (
    <LoginLayout>
      <form onSubmit={onLogin} style={{ width: '100%' }} >

        <Box alignItems="center" width="100%" style={{ textAlign: "-webkit-center" }}>

          <Stack sx={styles.form} direction="column" spacing={isMobile480 ? 2 : 4}
            padding={isMobile480 ? "16px 24px 16px 24px" : (isTablet768 || isTablet1024) ? "24px 60px 24px 60px" : "40px 60px 40px 60px"} style={{ textAlign: "-webkit-left" }}>

            <Box sx={styles.title}>{t("sistema.login.titulos.iniciarSesion")}</Box>

            {error && (
              <GTIWebAlert
                icon={<DoNotDisturbAlt />}
                severity="error"
                title={error.message}
                sx={{ maxWidth: "442px" }}
              />
            )}

            <GTIWebTextField
              name="username"
              value={loginData.username}
              onChange={handleChange}
              labelText={t("sistema.login.titulos.emailLabel")}
              placeholder={t("sistema.login.titulos.emailPlaceholder")}
              width="100%"
              disabled={isLoading}
              autoFocus={true}
              icon={loginData.userIcon}
              onBlur={validateInput}
              onFocus={validateInput}
              state={loginData.userState}
              autoComplete="email"

            />

            <GTIWebTextField
              name="password"
              disabled={isLoading}
              value={loginData.password}
              onChange={handleChange}
              labelText={t("sistema.login.titulos.passwordLabel")}
              placeholder={t("sistema.login.titulos.passwordPlaceholder")}
              type="password"
              legendText={loginData.passwordLegend}
              width="100%"
              state={loginData.passwordState}
              autoComplete="off"
              sx={
                {
                  '.MuiFormLabel-root': {
                    fontSize: "30px",
                  },
                }
              }
            />

            <GTIWebButton
              onlyIcon={isLoading}
              icon={isLoading && <CircularProgress size={30} />}
              disabled={isLoading}
              iconPosition="end"
              type="submit"
              text={t("sistema.login.acciones.boton")}
              sx={
                {
                  width: "100%",
                  fontSize: (isMobile480 || isTablet768) ? "14px" : isTablet1024 ? "16px" : "20px",
                }
              }
            />

            <Box sx={styles.forgotPassword}>
              <a 
                href={paths.RecuperarPassword}
                style={
                  {
                    fontSize: (isMobile480 || isTablet768) ? "14px" : isTablet1024 ? "16px" : "20px",
                  }
                }
                >
                {t("sistema.login.acciones.forgotPassword")}
              </a>
            </Box>
            {/* Funcionalidad pendiente para cambiar de sistema. Definida en zeplin */}
            {/* <Box sx={styles.cambiarDeSistema}>
              <a
                href={paths.SeleccionarSolucion}
                style={
                  {
                    fontSize: (isMobile480 || isTablet768) ? "14px" : isTablet1024 ? "16px" : "20px",
                  }
                }
                >
                  {t("sistema.login.acciones.cambiarDeSistema")}
              </a>
            </Box> */}

          </Stack>

        </Box>
      </form>
    </LoginLayout>
  );
};

/** Exportacion del componente para su invocacion en otros archivos */
export default Login;